/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * TdPrintQr — renders a QR code + the same payload as printable backup
 * text (4 letters per chunk, 15 spaces between, 4 chunks per row).
 *
 * Encryption path (#165 / app-backend#94): the vault passphrase is
 * encrypted to the system's ML-KEM-768 escrow public key with AAD
 * bound to the user's email. The resulting `RecoveryBundle` is JSON-
 * stringified + base64nopad-encoded for the QR payload. A
 * `TdForgotPass` wizard (#190) consumes the bundle on the recovery
 * side.
 *
 * Lit light-DOM component (#206). The `source` attribute drives a
 * custom async observer (NOT `@property`) because the observer's side
 * effect rewrites `this.source` with the encrypted bundle — feeding
 * @property's auto-reflection would create a feedback loop. The QR
 * payload + formatted-text content land via `${...}` text bindings, so
 * neither the passphrase nor the bundle can be parsed as HTML.
 *
 * QR error correction is pinned to "L" (#246): the escrow bundle encodes
 * to ~2.2 KB of base64, which exceeds the QR byte capacity at wa-qr-code's
 * default level "H" (1273 bytes) — the underlying qr-creator then paints
 * NOTHING, silently. Level "L" caps at 2953 bytes, which fits with margin;
 * the printed backup text is the redundancy channel, so the lowest ECC
 * level is acceptable here.
 */

import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { fetchSystemKemPublicKey } from '../services/tdfetch.js';
import { encryptForEscrow } from '../services/crypto/escrow.js';
import { defaultRandom } from '../services/random.js';
import { authService } from '../services/auth.js';
import { base64nopad } from '@scure/base';
import '@awesome.me/webawesome-pro/dist/components/qr-code/qr-code.js';
import { t, LocaleController } from '../services/i18n.js';

@customElement('td-print-qr')
export class TdPrintQr extends LitElement {
  @state() source = '';
  @state() passphrase = '';
  url = '/api/key/system/escrow-publickey';

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  override createRenderRoot(): HTMLElement {
    return this;
  }

  static override get observedAttributes(): string[] {
    return ['source'];
  }

  override async attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): Promise<void> {
    // Intentionally NOT calling `super.attributeChangedCallback` — the
    // `source` attribute is not registered via `@property`, so super's
    // reactive-property machinery would error on the unknown name.
    if (name === 'source' && oldValue !== newValue && newValue !== null) {
      this.source = newValue;
      this.passphrase = await this.processPassphrase(newValue);
    }
  }

  async processPassphrase(passphrase: string): Promise<string> {
    try {
      const email = authService.getEmail();
      if (!email) {
        throw new Error(
          'No authenticated user — vault-passphrase escrow requires a logged-in session'
        );
      }
      const systemKemPublicKey = await fetchSystemKemPublicKey(this.url);
      const bundle = encryptForEscrow(
        defaultRandom,
        systemKemPublicKey,
        passphrase,
        email
      );
      const uint8Array = new TextEncoder().encode(JSON.stringify(bundle));
      this.source = base64nopad.encode(uint8Array);
    } catch (error) {
      console.error('Error encrypting passphrase:', error);
      this.source = error instanceof Error ? error.message : String(error);
    }
    // Return the original passphrase
    return passphrase;
  }

  formatSourceText(text: string): string {
    if (!text) return '';

    let formatted = '';
    for (let i = 0; i < text.length; i += 4) {
      const chunk = text.slice(i, i + 4);
      formatted += chunk + ' '.repeat(15);

      // Add newline after every 4 chunks (16 letters)
      if ((i + 4) % 16 === 0) {
        formatted += '\n';
      }
    }
    return formatted;
  }

  override render(): TemplateResult {
    return html`
      <div class="print-qr-container">
        <wa-details summary=${t('printqr.summary')}>
          <h1 class="passphrase-display">${this.passphrase}</h1>
        </wa-details>
        <div class="qr-section">
          <wa-card>
            <h3>${t('printqr.qrTitle')}</h3>
            <div id="qr-code-display" class="wa-margin-l">
              ${
                this.passphrase && this.source
                  ? html`<wa-qr-code
                      value=${this.source}
                      size="450"
                      error-correction="L"
                    ></wa-qr-code>`
                  : nothing
              }
            </div>
          </wa-card>
        </div>
        <wa-card class="text-section">
          <h3>${t('printqr.backupTitle')}</h3>
          <pre id="formatted-text">${this.formatSourceText(this.source)}</pre>
        </wa-card>
      </div>
    `;
  }
}
